import {
  Activity,
  BookOpen,
  BriefcaseBusiness,
  ChefHat,
  ClipboardList,
  CloudOff,
  Database,
  Clock3,
  DollarSign,
  FileText,
  Fuel,
  GitBranch,
  LayoutDashboard,
  LogOut,
  Megaphone,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  Store,
  UserCog
} from "lucide-react";
import type { ReactNode } from "react";
import { useAuthUser } from "../../auth/AuthProvider";
import type { ApiStatus } from "../../services/apiClient";
import { SpotlightNavbar, type SpotlightNavigationItem, type SpotlightSubNavigationItem } from "./SpotlightNavbar";

export type ViewId =
  | "landing"
  | "station-analytics"
  | "reports"
  | "reports-bistro"
  | "reports-order-planning"
  | "reports-gas-prices"
  | "profit-loss"
  | "business-analysis"
  | "marketing"
  | "marketing-station-sns"
  | "access-admin"
  | "dashboard"
  | "runs"
  | "job-detail"
  | "health-map"
  | "settings"
  | "about";

interface NavigationItem {
  id: ViewId;
  label: string;
  icon: typeof LayoutDashboard;
  scope: "station" | "reports" | "profit-loss" | "business-analysis" | "marketing" | "health" | "settings" | "access-admin" | "admin" | "docs";
}

const navigationItems: NavigationItem[] = [
  { id: "station-analytics", label: "Stations", icon: Store, scope: "station" },
  { id: "reports", label: "Reports", icon: FileText, scope: "reports" },
  { id: "profit-loss", label: "Profit & Loss", icon: DollarSign, scope: "profit-loss" },
  { id: "business-analysis", label: "Business Analysis", icon: BriefcaseBusiness, scope: "business-analysis" },
  { id: "marketing", label: "Marketing", icon: Megaphone, scope: "marketing" },
  { id: "dashboard", label: "Health Monitor", icon: LayoutDashboard, scope: "health" },
  { id: "runs", label: "Runs Monitoring", icon: Activity, scope: "health" },
  { id: "health-map", label: "Health Map", icon: GitBranch, scope: "health" },
  { id: "settings", label: "Settings / Jobs", icon: SlidersHorizontal, scope: "settings" },
  { id: "access-admin", label: "Access Admin", icon: UserCog, scope: "access-admin" },
  { id: "about", label: "Documentation", icon: BookOpen, scope: "docs" }
];

const healthSubnavIds: ViewId[] = ["runs", "health-map", "settings"];
const reportsSubnavItems: Array<{ id: ViewId; label: string; icon: typeof LayoutDashboard }> = [
  { id: "reports-bistro", label: "Bistro Analysis", icon: ChefHat },
  { id: "reports-order-planning", label: "C-store Vendor Invoice", icon: ClipboardList },
  { id: "reports-gas-prices", label: "Competitor Gas Prices", icon: Fuel }
];
const reportsSubnavIds = reportsSubnavItems.map((item) => item.id);
const marketingSubnavItems: Array<{ id: ViewId; label: string; icon: typeof LayoutDashboard }> = [
  { id: "marketing", label: "Overview", icon: LayoutDashboard },
  { id: "marketing-station-sns", label: "Station SNS", icon: Store }
];
const marketingSubnavIds = marketingSubnavItems.map((item) => item.id);

interface AppShellProps {
  activeView: ViewId;
  apiStatus: ApiStatus;
  children: ReactNode;
  isRefreshing: boolean;
  lastUpdatedAt: string;
  selectedStationSk?: number;
  onNavigate: (view: ViewId) => void;
  onNavigateLanding: () => void;
  onNavigateStation: (stationSk?: number) => void;
  onRefresh: () => void;
}

export function AppShell({
  activeView,
  apiStatus,
  children,
  isRefreshing,
  lastUpdatedAt,
  selectedStationSk,
  onNavigate,
  onNavigateLanding,
  onNavigateStation,
  onRefresh
}: AppShellProps) {
  const { hasAnyRole, portalContext, userLabel, signOut } = useAuthUser();
  const canOperate = portalContext?.features.healthOperations || hasAnyRole(["HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const canAdmin = portalContext?.features.healthAdmin || hasAnyRole(["HealthDashboard.Admin"]);
  const canAccessAdmin = portalContext?.features.stationAccessAdmin || hasAnyRole(["StationAnalytics.AccessAdmin", "HealthDashboard.Admin"]);
  const canViewReports = Boolean(portalContext?.features.reports);
  const canViewProfitLoss = Boolean(portalContext?.features.profitLoss);
  const canViewBusinessAnalysis = Boolean(portalContext?.features.businessAnalysis);
  const canViewMarketing = Boolean(portalContext?.features.marketingAnalytics) || hasAnyRole(["Marketing.Analytics"]);
  const canViewHealth = portalContext?.features.healthMonitoring || hasAnyRole(["HealthDashboard.Reader", "HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const canViewSettings = canAdmin || Boolean(portalContext?.features.portalExecutiveMember);
  const canViewStations = Boolean(portalContext?.features.stationAnalytics);
  const stationSubnavItems = [...(portalContext?.stations || [])]
    .sort((left, right) => (left.stationName || left.businessUnitName || "").localeCompare(right.stationName || right.businessUnitName || ""));
  const visibleNavigationItems = navigationItems.filter((item) => {
    if (item.scope === "health") {
      return canViewHealth;
    }
    if (item.scope === "admin") {
      return canAdmin;
    }
    if (item.scope === "settings") {
      return canViewSettings;
    }
    if (item.scope === "access-admin") {
      return canAccessAdmin;
    }
    if (item.scope === "reports") {
      return true;
    }
    if (item.scope === "profit-loss") {
      return canViewProfitLoss;
    }
    if (item.scope === "business-analysis") {
      return canViewBusinessAnalysis;
    }
    if (item.scope === "marketing") {
      return canViewMarketing;
    }

    return item.scope === "station" ? canViewStations || item.id === "station-analytics" : true;
  });
  const healthSubnavItems = visibleNavigationItems.filter((item) => healthSubnavIds.includes(item.id));
  const isStationAnalyticsView = activeView === "station-analytics";
  const isProfitLossView = activeView === "profit-loss";
  const isBistroReportsView = activeView === "reports" || activeView === "reports-bistro";
  const isOrderPlanningView = activeView === "reports-order-planning";
  const isGasPricesView = activeView === "reports-gas-prices";
  const isReportsView = isBistroReportsView || isOrderPlanningView || isGasPricesView;
  const isMarketingView = marketingSubnavIds.includes(activeView);
  const primaryNavigationItems: SpotlightNavigationItem[] = visibleNavigationItems
    .filter((item) => !healthSubnavIds.includes(item.id) && item.id !== "settings")
    .map((item) => {
      const active = item.id === "station-analytics"
        ? isStationAnalyticsView
        : item.id === "reports"
          ? isReportsView
          : item.id === "marketing"
            ? isMarketingView
            : item.id === "dashboard"
              ? activeView === "dashboard" || healthSubnavIds.includes(activeView) || activeView === "job-detail"
              : activeView === item.id;
      return {
        id: item.id,
        label: item.label,
        active,
        onClick: () => {
          if (item.id === "station-analytics") {
            onNavigateStation(undefined);
          } else if (item.id === "reports") {
            onNavigate(canViewReports ? "reports-bistro" : "reports-gas-prices");
          } else {
            onNavigate(item.id);
          }
        }
      };
    });
  const spotlightSubNavigationItems: SpotlightSubNavigationItem[] = isStationAnalyticsView
    ? [
        { id: "all-stations", label: "All Stations", active: selectedStationSk === undefined, onClick: () => onNavigateStation(undefined) },
        ...stationSubnavItems.map((station) => ({
          id: `station-${station.stationSk}`,
          label: station.stationName || station.businessUnitName || `Station ${station.stationSk}`,
          active: selectedStationSk === station.stationSk,
          onClick: () => onNavigateStation(station.stationSk)
        }))
      ]
    : activeView === "dashboard" || activeView === "runs" || activeView === "job-detail" || activeView === "health-map" || activeView === "settings"
      ? [
          { id: "health-overview", label: "Overview", active: activeView === "dashboard", onClick: () => onNavigate("dashboard") },
          ...healthSubnavItems.map((item) => ({
            id: item.id,
            label: item.id === "settings" ? "Settings / Jobs" : item.label,
            active: activeView === item.id || (item.id === "runs" && activeView === "job-detail"),
            onClick: () => onNavigate(item.id)
          }))
        ]
      : isReportsView
        ? reportsSubnavItems
            .filter((item) => canViewReports || item.id === "reports-gas-prices")
            .map((item) => ({
              id: item.id,
              label: item.label,
              active: activeView === item.id || (activeView === "reports" && item.id === "reports-bistro"),
              onClick: () => onNavigate(item.id)
            }))
        : isMarketingView
          ? marketingSubnavItems.map((item) => ({
              id: item.id,
              label: item.label,
              active: activeView === item.id,
              onClick: () => onNavigate(item.id)
            }))
          : [];
  const dataSource = isGasPricesView || ((isStationAnalyticsView || isProfitLossView || isBistroReportsView || isOrderPlanningView) && apiStatus.dataMode === "sql")
    ? {
        label: "Semantic Model",
        modeLabel: isGasPricesView ? "EDW_GasPrices" : isProfitLossView ? "EDW_ProfitNLoss" : isBistroReportsView ? "EDW_SalesPromoTransaction" : isOrderPlanningView ? "EDW_OrderPlanning" : "EDW_SalesInv",
        sourceClass: "source-powerbi"
      }
    : {
        label: apiStatus.dataMode === "sql" ? "Azure SQL" : apiStatus.dataMode === "mock" ? "Mock" : "Protected",
        modeLabel: apiStatus.dataMode === "sql" ? "SQL MODE" : apiStatus.dataMode === "mock" ? "MOCK MODE" : "PROTECTED",
        sourceClass: `source-${apiStatus.dataMode}`
      };
  const apiBannerText = apiStatus.dataMode === "unknown"
    ? "Authentication is required for dashboard API access."
    : apiStatus.dataMode === "mock"
      ? "API unavailable. Local mock fallback is active."
      : "Dashboard refresh completed with warnings.";

  return (
    <div className="app-shell" data-view={activeView}>

      <div className="workspace">
        <header className="top-header">
          <div>
            <p>Data Integration Health Monitoring Dashboard</p>
            <h1>DataOps Health Command Center</h1>
          </div>
          <div className="header-actions">
            <span className="header-user-greeting" title={`Welcome, ${userLabel}`}>
              Welcome, <strong>{userLabel}</strong>
            </span>
            <span className={`data-source-pill ${dataSource.sourceClass}`}>
              <Database size={15} aria-hidden="true" />
              {dataSource.label}
            </span>
            <span className="mock-badge">{dataSource.modeLabel}</span>
            <span className="timestamp-pill">
              <Clock3 size={15} aria-hidden="true" />
              {lastUpdatedAt}
            </span>
            <div className="header-user-actions">
              {canOperate && (
                <button className={`icon-button${isRefreshing ? " is-refreshing" : ""}`} disabled={isRefreshing} onClick={onRefresh} title="Refresh execution history" type="button">
                  <RefreshCw size={18} aria-hidden="true" />
                </button>
              )}
              {canViewSettings && (
                <button className="icon-button" title="Settings" type="button" onClick={() => onNavigate("settings")}>
                  <Settings size={18} aria-hidden="true" />
                </button>
              )}
              <button className="icon-button" aria-label="Sign out" title="Sign out" type="button" onClick={signOut}>
                <LogOut size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>

        {!apiStatus.apiAvailable && (
          <div className="api-banner" role="status">
            <CloudOff size={17} aria-hidden="true" />
            <span>{apiBannerText}</span>
            {apiStatus.message && <small>{apiStatus.message}</small>}
          </div>
        )}

        <SpotlightNavbar
          activeView={activeView}
          items={primaryNavigationItems}
          onNavigateLanding={onNavigateLanding}
          subItems={spotlightSubNavigationItems}
        />

        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
